<template>
  <div class="page-container-wrapper">
    <loader v-if="dataLoading"/>
    <template v-else>
      <div class="page-container-header d-flex align-items-center">
          <h2 class="text-md mb-0">{{ $t('message.Guideline.publications') }}</h2>
          <b-button v-if="isSuperUser" variant="info" class="mr-2 ml-auto" @click="$router.push('/')">{{ $t('message.return') }}</b-button>
      </div>

      <div class="progress-step-container page-container">
        <div><div class="progress-step" :class="{'progress-step--passed' : step >= 0}"><span @click="setStep(0)" class="progress-step__value">1</span><span class="progress-step__text">Base Data</span></div></div>
        <div><div class="progress-step" :class="{'progress-step--passed' : step >= 1}"><span @click="setStep(1)" class="progress-step__value">2</span><span class="progress-step__text">Involved parties</span></div></div>
        <div><div class="progress-step" :class="{'progress-step--passed' : step >= 2}"><span @click="setStep(2)" class="progress-step__value">3</span><span class="progress-step__text">Specific information</span></div></div>
        <div><div class="progress-step" :class="{'progress-step--passed' : step >= 3}"><span @click="setStep(3)" class="progress-step__value">4</span><span class="progress-step__text">Publication</span></div></div>
        <div><div class="progress-step" :class="{'progress-step--passed' : step >= 4}"><span @click="setStep(4)" class="progress-step__value">5</span><span class="progress-step__text">Budget</span></div></div>
        <div><div class="progress-step" :class="{'progress-step--passed' : step >= 5}"><span @click="setStep(5)" class="progress-step__value">6</span><span class="progress-step__text">Conflict of interest</span></div></div>
        <div><div class="progress-step" :class="{'progress-step--passed' : step >= 6}"><span @click="setStep(6)" class="progress-step__value">7</span><span class="progress-step__text">Finalising</span></div></div>
      </div>

      <div class="page-container guidelines-editor" id="page-container">

        <!--
        <div class="page-container-toolbar">
          <template v-if="isSuperUser && guideline.id != undefined">
            <b-button variant="outline-secondary" size="sm" class="mr-2 ml-auto" @click="update(false)">{{ $t('message.update') }}</b-button>
          </template>
        </div>
        -->

        <b-alert v-show="dataError" class="mt-4" show variant="danger">{{ $t('message.connectionError') }}</b-alert>

        <form>
          <div class="row">
            <div class="col-md-12">
              <div>

                <!-- ++++++++++++++ STEP 0 -->

                <b-card :title="$t('message.Guideline.section1')" class="col-12 mb-2" v-if="step === 0">
                  <b-row class="my-1">
                    <b-col sm="3"><label class="col-form-label">{{ $t('message.Guideline.name') }} <var class="text-danger">*</var>
                      <br>{{ $t('message.Guideline.max20words') }}
                      </label>
                    </b-col>
                    <b-col sm="9">
                      <b-form-input
                        :class="errors.first('guideline.name')?'is-invalid':''"
                        v-model="guideline.name"
                        v-validate="'required|max-words:20'"
                        data-vv-name="guideline.name"
                        type="text"
                        :placeholder="$t('message.Guideline.namePlaceholder')"
                        data-vv-scope="step.0"
                        ></b-form-input>
                      <small>{{ $t('message.Guideline.wordCount') }} {{ wordCounter(guideline.name) }}</small>
                      <div class="invalid-feedback">
                        <p v-if="errors.firstByRule('guideline.name', 'max-words')"> {{ errors.firstByRule('guideline.name', 'max-words') }} </p>
                        <template v-else>{{ $t('message.incorrectValue') }}</template>
                      </div>
                    </b-col>
                  </b-row>

                  <template v-if="isSuperUser">
                    <b-row class="my-1">
                      <b-col sm="3">
                        <label class="col-form-label">{{ $t('message.Guideline.phase') }}</label>
                      </b-col>
                      <b-col sm="9">
                        <v-select
                          :state="errors.first('guideline.phase')?'invalid':'true'"
                          v-model="guidelinePhase"
                          :options="guidelinePhaseOptions"
                          v-validate="{ required: true }"
                          data-vv-name="guidelinePhase"
                          data-vv-scope="step.0"
                          >
                        </v-select>
                        <div v-if="errors.first('guideline.phase')" class="text-danger">{{ $t('message.fieldRequiredInfo') }}</div>
                      </b-col>
                    </b-row>
                  </template>

                  <b-row class="my-1" v-if="guideline.created_at">
                    <b-col sm="3"><label class="col-form-label">{{ $t("message.createdAt") }}</label></b-col>
                    <b-col sm="9"><label class="col-form-label">{{ guideline.created_at }}</label></b-col>
                  </b-row>

                  <b-row class="my-1" v-if="guideline.updated_at">
                    <b-col sm="3"><label class="col-form-label">{{ $t("message.updatedAt") }}</label></b-col>
                    <b-col sm="9"><label class="col-form-label">{{ guideline.updated_at }}</label></b-col>
                  </b-row>

                  <b-row class="my-1">
                    <b-col sm="3">
                      <label class="col-form-label">{{ $t('message.Guideline.entity') }} <var class="text-danger">*</var></label>
                    </b-col>
                    <b-col sm="9">
                      <v-select
                          :state="errors.first('guideline.abbreviation_of_committee')?'invalid':'true'"
                          v-model="guideline.abbreviation_of_committee"
                          :options="guidelineTypes"
                          v-validate="{ required: true }"
                          data-vv-name="guideline.abbreviation_of_committee"
                          data-vv-scope="step.0"
                          key="abbreviation-of-committee">
                      </v-select>
                      <div v-if="errors.first('guideline.abbreviation_of_committee')" class="text-danger">{{ $t('message.fieldRequiredInfo') }}</div>
                    </b-col>
                  </b-row>

                  <b-row class="my-1 mt-4">
                    <b-col sm="3">
                      <label class="col-form-label">{{ $t('message.Guideline.type') }} <var class="text-danger">*</var></label>
                    </b-col>

                    <b-col sm="9">
                      <b-form-radio-group :invalid-feedback="'test invalid feedback'" :state="errors.first('guideline.type')?'invalid':'true'"
                                          data-vv-scope="step.0"
                                          v-model="guideline.type" :options="typesOfManuscript" name="type" stacked v-validate="'required'" data-vv-name="guideline.type">
                      </b-form-radio-group>
                    </b-col>
                  </b-row>

                  <b-row class="my-1 mt-4">
                    <b-col sm="3">
                      <label class="col-form-label">
                        {{ $t('message.Guideline.kindsOfWorkLabel') }} <var class="text-danger">*</var>
                        <template v-if="guideline.kind_of_work == 1">
                          <br> {{ $t('message.Guideline.max1000') }}
                        </template>
                      </label>
                    </b-col>

                     <b-col sm="9">
                      <b-form-group>
                        <b-form-radio-group
                          name="kind_of_work"
                          :state="errors.first('guideline.kind_of_work')?'invalid':'true'"
                          v-model="guideline.kind_of_work"
                          data-vv-scope="step.0"
                          :options="kindsOfWork"
                          stacked
                          v-validate="{ required: true }"
                          data-vv-name="guideline.kind_of_work"
                        >
                        </b-form-radio-group>
                      </b-form-group>

                      <b-form-textarea
                        id="kind_of_work_joint_description"
                        v-show="guideline.kind_of_work == 1"
                        v-model="guideline.kind_of_work_joint_description"
                        :placeholder="$t('message.Guideline.kindsOfWorkJointDetaild')"
                        :rows="3"
                        :max-rows="6"
                        v-validate="guideline.kind_of_work == 1 ? 'required|max-words:1000' : ''"
                        :state="guideline.kind_of_work == 1 ? (errors.first('guideline.kind_of_work_joint_description') ? 'invalid' : 'true') : null"
                        data-vv-name="guideline.kind_of_work_joint_description"
                        data-vv-scope="step.0"
                      ></b-form-textarea>
                      <div v-show="guideline.kind_of_work == 1">
                        <div>{{ $t('message.Guideline.wordCount') }} {{ wordCounter(guideline.kind_of_work_joint_description) }}</div>
                        <div class="invalid-feedback">
                          <p v-if="errors.firstByRule('guideline.kind_of_work_joint_description', 'max-words')"> {{ errors.firstByRule('guideline.kind_of_work_joint_description', 'max-words') }} </p>
                          <template v-else>{{ $t('message.incorrectValue') }}</template>
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </b-card>



                <!-- ++++++++++++++ STEP 1 -->
                <involved-parties :guideline="guideline" :active="step === 1"></involved-parties>

                <!-- ++++++++++++++ STEP 2 -->

                <b-card :title="$t('message.Guideline.section3')" class="col-12 mb-2"  v-if="step === 2">
                  <b-row class="my-4">
                    <b-col sm="3">
                      <label class="col-form-label">
                        {{ $t('message.Guideline.description') }} <var class="text-danger">*</var>
                         <br> {{ $t('message.Guideline.max1000') }}
                      </label>
                    </b-col>
                    <b-col sm="9">
                      <b-form-textarea
                           v-model="guideline.description"
                           :rows="5"
                           :max-rows="6"
                           v-validate="'required|max-words:1000'"
                           :state="errors.first('guideline.description')?'invalid':'true'"
                           data-vv-name="guideline.description"
                           data-vv-scope="step.2"
                           :placeholder="$t('message.Guideline.descriptionPlaceholder')"
                        ></b-form-textarea>
                        <div>{{ $t('message.Guideline.wordCount') }} {{ wordCounter(guideline.description) }}</div>
                        <div class="invalid-feedback">
                          <p v-if="errors.firstByRule('guideline.description', 'max-words')"> {{ errors.firstByRule('guideline.description', 'max-words') }} </p>
                          <template v-else>{{ $t('message.incorrectValue') }}</template>
                        </div>
                    </b-col>
                  </b-row>

                  <b-row class="mt-5">
                    <b-col sm="3">
                      <label class="col-form-label">
                        {{ $t('message.Guideline.draftChanges')}}
                        <br> {{ $t('message.Guideline.draftChangesInfo') }}
                        <br>
                        <br> {{ $t('message.Guideline.max1000') }}
                      </label>
                    </b-col>
                    <b-col sm="9">
                      <b-form-textarea
                          v-model="guideline.changes"
                          :placeholder="$t('message.Guideline.draftChangesPlaceholder')"
                          :rows="4"
                          :max-rows="6"
                          v-validate="'max-words:1000'"
                          data-vv-name="guideline.changes"
                          data-vv-scope="step.2"
                          :state="errors.first('guideline.changes')?'invalid':null">
                      </b-form-textarea>
                      <div>{{ $t('message.Guideline.wordCount') }} {{ wordCounter(guideline.changes) }}</div>
                      <div class="invalid-feedback">
                        <p v-if="errors.firstByRule('guideline.changes', 'max-words')"> {{ errors.firstByRule('guideline.changes', 'max-words') }} </p>
                        <template v-else>{{ $t('message.incorrectValue') }}</template>
                      </div>
                    </b-col>
                  </b-row>
                </b-card>


                <!-- ++++++++++++++ STEP 3 -->

                <b-card :title="$t('message.Guideline.section4')" class="col-12 mb-2"  v-if="step === 3">
                  <b-row class="my-1" v-if="false">
                    <b-col sm="3"><label class="col-form-label">{{ $t("message.Guideline.publicationInfo") }} <!-- <var class="text-danger">*</var> --></label></b-col>
                    <b-col sm="9">
  <!--                     <b-form-textarea
                       v-model="guideline.publication_description"
                       :rows="3"
                       :max-rows="6"
                       v-validate="'required|max:1000'"
                       :state="errors.first('guideline.publication_description')?'invalid':'true'"
                       data-vv-name="guideline.publication_description"
                       data-vv-scope="step.3"
                       key="publication-description">
                      </b-form-textarea> -->
                      Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum.
                    </b-col>
                  </b-row>

                  <b-row class="my-1 mt-4">
                    <b-col sm="3">
                      <label class="col-form-label">{{ $t('message.Guideline.destination') }} <var class="text-danger">*</var></label>
                    </b-col>

                     <b-col sm="9">
                      <b-form-group>
                        <b-form-radio-group
                          name="destination"
                          :state="errors.first('guideline.publication')?'invalid':'true'"
                          v-model="guideline.publication"
                          stacked
                          v-validate="'required'"
                          data-vv-name="guideline.publication"
                          data-vv-scope="step.3"
                          >

                          <b-form-radio
                            value="0">
                            {{ $t('message.Guideline.destinationsOfManuscript.0') }}
                          </b-form-radio>

                          <b-form-radio
                            value="1">
                            {{ $t('message.Guideline.destinationsOfManuscript.1') }}
                          </b-form-radio>

                          <b-form-radio
                            value="2">
                            {{ $t('message.Guideline.destinationsOfManuscript.2') }}
                          </b-form-radio>
                        </b-form-radio-group>

                        <template v-if="guideline.publication == 2">
                          <b-row class="my-1 ml-1">
                            <b-col sm="3">
                              <label class="col-form-label">
                                {{ $t('message.Guideline.indicateJournal') }} <var class="text-danger">*</var>
                              </label>
                            </b-col>
                            <b-col sm="9">
                              <b-form-input v-model="guideline.publication_target_other_journal"
                                type="text"
                                :state="errors.first('guideline.publication_target_other_journal')?'invalid':'true'" 
                                class="w-100"
                                v-validate="'required'"
                                data-vv-scope="step.3"
                                data-vv-name="guideline.publication_target_other_journal"
                                :placeholder="$t('message.Guideline.otherJournalPlaceholder')"></b-form-input>
                            </b-col>
                          </b-row>

                          <b-row class="my-1 ml-1">
                            <b-col sm="3">
                              <label class="col-form-label">
                                {{ $t('message.Guideline.indicatePublisher') }} <var class="text-danger">*</var>
                              </label>
                            </b-col>
                            <b-col sm="9">
                              <b-form-input v-model="guideline.publication_target_other_journal_publisher"
                                type="text"
                                :state="errors.first('guideline.publication_target_other_journal_publisher')?'invalid':'true'"
                                class="w-100"
                                v-validate="'required'"
                                data-vv-scope="step.3"
                                data-vv-name="guideline.publication_target_other_journal_publisher"
                                :placeholder="$t('message.Guideline.otherJournalPlaceholder')"></b-form-input>
                            </b-col>
                          </b-row>
                        </template>
                      </b-form-group>                                      
                    </b-col>
                  </b-row>
                  <b-row class="my-1 mt-4">
                    <b-col sm="3">
                      <label class="col-form-label">{{ $t('message.Guideline.editorInChiefContacted') }}</label>
                    </b-col>
                    <b-col sm="9">
                      <b-form-checkbox v-model="guideline.editorinchief_contacted"
                        value="1"
                        unchecked-value="0"
                        data-vv-scope="step.5"
                        data-vv-name="guideline.editorinchief_contacted"
                        :state="errors.first('guideline.editorinchief_contacted')?'invalid':'success'">
                      </b-form-checkbox>
                    </b-col>
                    <b-alert class="clearfix mt-4" show>{{ $t('message.Guideline.editorInChiefContactedDescription') }}</b-alert>
                  </b-row>
                </b-card>


                <!-- ++++++++++++++ STEP 4 -->

                <b-card :title="$t('message.Guideline.section5')" class="col-12 mb-2"  v-if="step === 4">
                  <b-row class="mb-2">
                    <b-col>
                      <h6>{{ $t('message.Guideline.Milestones.label') }}</h6>
                      <b-alert variant="info" show>
                        <h6 class="alert-heading">{{ $t('message.Guideline.clickToAdd') }}</h6>
                        {{ $t('message.Guideline.clickToAddInfo') }}
                      </b-alert>
                    </b-col>
                  </b-row>
                  <b-row class="mb-5">
                    <b-col sm="3">
                      <div>
                        <b-button
                                class="mt-1 w-100"
                                variant="primary"
                                @click="showMilestoneModal('meeting')">
                          {{ $t('message.Guideline.Milestones.meeting') }}
                        </b-button>
                        <b-form-checkbox class="filter-checkbox" v-model="milestone_meeting_check" v-if="needMilestoneCheck('meeting')">
                          <span>{{ $t('message.Guideline.Milestones.noMilestoneSet') }}</span>
                        </b-form-checkbox>
                        <p v-show="errors.has('milestone.meeting.not_set') && needMilestoneCheck('meeting') && !milestone_meeting_check" class="text-danger">{{errors.first('milestone.meeting.not_set')}}</p>
                      </div>

                      <div>
                        <b-button
                                class="mt-3 w-100"
                                variant="secondary"
                                @click="showMilestoneModal('meta')"
                                :disabled="this.metaAnalysisCounter > 0"
                                v-html="$t('message.Guideline.Milestones.metaHtml')">
                        </b-button>
                        <b-form-checkbox class="filter-checkbox" v-model="milestone_meta_check"  v-if="needMilestoneCheck('meta')">
                          <span>{{ $t('message.Guideline.Milestones.noMilestoneSet') }}</span>
                        </b-form-checkbox>
                        <p v-show="errors.has('milestone.meta.not_set') && needMilestoneCheck('meta') && !milestone_meta_check" class="text-danger">{{errors.first('milestone.meta.not_set')}}</p>
                      </div>

                      <div>
                        <b-button
                                class="mt-3 w-100"
                                variant="success"
                                @click="showMilestoneModal('medical')"
                                v-html="$t('message.Guideline.Milestones.methodologistHtml')">
                        </b-button>
                        <b-form-checkbox class="filter-checkbox" v-model="milestone_medical_check"  v-if="needMilestoneCheck('medical')">
                          <span>{{ $t('message.Guideline.Milestones.noMilestoneSet') }}</span>
                        </b-form-checkbox>
                        <p v-show="errors.has('milestone.medical.not_set') && needMilestoneCheck('medical') && !milestone_medical_check" class="text-danger">{{errors.first('milestone.medical.not_set')}}</p>
                      </div>

                      <div>
                        <b-button
                                class="mt-3 w-100"
                                variant="danger"
                                @click="showMilestoneModal('publication')"
                                :disabled="publicationFeeCounter > 0"
                        >
                          {{ $t("message.Guideline.Milestones.publication") }}
                        </b-button>
                        <b-form-checkbox class="filter-checkbox" v-model="milestone_publication_check"  v-if="needMilestoneCheck('publication')">
                          <span>{{ $t('message.Guideline.Milestones.noMilestoneSet') }}</span>
                        </b-form-checkbox>
                        <p v-show="errors.has('milestone.publication.not_set') && needMilestoneCheck('publication') && !milestone_publication_check" class="text-danger">{{errors.first('milestone.publication.not_set')}}</p>
                      </div>
                    </b-col>

                    <b-col sm="9">
                      <b-table striped hover :sort-by="'start'" v-if="guidelineBudgets.length > 0" id="budgetsTable" ref="budgetsTable" :items="guidelineBudgets" :fields="budgetFields">
                        <template slot="table-colgroup">
                          <col width="110px">
                          <col>
                          <col width="150px">
                          <col width="150px">
                        </template>
                        <template slot="budget" slot-scope="data">
                          {{ data.item.budget }} &euro;
                        </template>

                        <template slot="start" slot-scope="data">
                          {{ moment(data.item.start).format('DD-MM-YYYY') }}
                        </template>
                        <template slot="type" slot-scope="data">
                          <b-badge
                            class="mt-1"
                            variant="primary"
                            v-if="data.item.type == 'meeting'"
                            >{{ $t('message.Guideline.Milestones.meeting') }}</b-badge>

                          <b-badge
                            class="mt-3"
                            variant="secondary"
                            v-if="data.item.type == 'meta'"
                            v-html="$t('message.Guideline.Milestones.metaHtml')">
                            </b-badge>
                          <b-badge
                            class="mt-3"
                            variant="success"
                            v-if="data.item.type == 'medical'"
                            v-html="$t('message.Guideline.Milestones.methodologistHtml')">
                          </b-badge>
                          <b-badge
                            class="mt-3"
                            variant="danger"
                            v-if="data.item.type == 'publication'">
                            {{ $t("message.Guideline.Milestones.publication") }}</b-badge>
                        </template>

                        <template slot="actions" slot-scope="data">
                          <b-row>
                              <b-col>
                                <a @click.prevent="editMilestone(data.item.id)" class="i-con-h-a">
                                    <span class="nav-icon">
                                      <i class="i-con i-con-edit"></i>
                                    </span>
                                </a>
                              </b-col>
                              <b-col>
                                <a @click.prevent="deleteMilestone(data.item.id)" class="i-con-h-a">
                                    <span class="nav-icon">
                                      <i class="i-con i-con-trash"><i></i></i>
                                    </span>
                                </a>
                              </b-col>
                          </b-row>
                        </template>
                      </b-table>
                    </b-col>
                  </b-row>

                  <b-row v-show="guidelineBudgets.length > 0">
                    <b-col sm="9" offset-sm="3" class="text-right">
                      {{ $t('message.Guideline.Milestones.deleteInfo') }}
                    </b-col>
                  </b-row>

                    <b-row>
                        <b-col>
                            <!-- TODO @martin should we add the milestone phases view to the edit guideline aswell -->
                            <milestones v-if="guidelineCreatedAt != null" :title="$t('message.Guideline.Milestones.timeline')" :budgets="guideline.budget" :createdDate="guidelineCreatedAt"></milestones>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col>
                          <b-alert variant="info" show>
                            {{ $t('message.Guideline.Milestones.info') }}
                          </b-alert>
                        </b-col>
                    </b-row>
                </b-card>


                <!-- ++++++++++++++ STEP 5 -->

                <b-card :title="$t('message.Guideline.section6')" class="col-12 mb-2"  v-if="step === 5">

                  <!-- note to future self: theres a second copy in the backend project for the mails! /-->
                  <a target="_blank" href="/pdf/2026_DOI_Form_GL.pdf">{{ $t('message.Guideline.downloadConflictOfInterestPDF') }}</a>
                  <div>
                    <b-form-checkbox 
                      v-model="guideline.send_coi_email" 
                      readonly
                      value="1"
                      unchecked-value="0"
                      v-validate="{ required: true, min_value: 1 }"
                      data-vv-scope="step.5"
                      data-vv-name="guideline.send_coi_email"
                      :state="errors.first('guideline.send_coi_email')?'invalid':'success'">
                      <var class="text-danger">*</var> {{ $t('message.Guideline.sendConflictOfInterestEmail') }}
                    </b-form-checkbox>

                    <p v-if="errors.first('guideline.send_coi_email')"><b-alert variant="danger" show>{{ $t('message.Guideline.coiErrorInfo') }}</b-alert></p>
                  </div>

                  <b-alert class="clearfix mt-4" show v-if="guideline.send_coi_email == 1">{{ $t('message.Guideline.sendConflictOfInterestEmailInfo') }}</b-alert>

                  <template v-if="isSuperUser && guideline.coi_users && guideline.coi_users.length > 0">
                    <b-table striped hover :items="guideline.coi_users" :fields="['name', 'email', 'conflict_of_interest']">
                      <template slot="conflict_of_interest" slot-scope="data">
                        <template v-if="data.item.conflict_of_interest">
                          {{ $t('message.Guideline.coiAccepted') }} 
                        </template>
                        <template v-else>
                          {{ $t('message.Guideline.coiNotAccepted') }} 
                        </template>
                      </template>
                    </b-table>

                    <b-button v-if="guideline.phase == guidelinePhases.preApproved" variant="primary" @click="$router.push('/guidelines/' + guideline.id + '/coi-managment')">{{ $t('message.Guideline.coiManagment') }}</b-button>
                  </template>
                </b-card>

                <!-- ++++++++++++++ STEP 6 -->

                <b-card :title="$t('message.Guideline.section7')" class="col-12 mb-2"  v-if="step === 6">
                  <b-row>
                  <b-form-checkbox id="co_agreement"
                                   v-model="guideline.co_agreement"
                                   v-validate="{ required: true, min_value: 1 }"
                                   data-vv-scope="step.final"
                                   data-vv-name="guideline.co_agreement"
                                   value="1"
                                   unchecked-value="0"
                                   :state="errors.first('guideline.co_agreement')?'invalid':'success'"
                                   key="co-agreement">
                    <var class="text-danger">*</var> {{ $t('message.Guideline.coaAgreement') }}
                  </b-form-checkbox>
                  </b-row>

                  <b-row>
                  <b-form-checkbox id="publication_rules"
                                   v-model="guideline.publication_rules"
                                   v-validate="{ required: true, min_value: 1 }"
                                   data-vv-scope="step.final"
                                   data-vv-name="guideline.publication_rules"
                                   value="1"
                                   unchecked-value="0"
                                   :state="errors.first('guideline.publication_rules')?'invalid':'success'"
                                   key="publication-rules">
                    <var class="text-danger">*</var> {{ $t('message.Guideline.acceptedPublicationRulesAndManuals') }}
                    <a target="_blank" href="https://drive.google.com/file/d/1n3o8HVkZlLNFfmCPljKoTl6jjEpfsLfB/view">{{ $t('message.Guideline.acceptedPublicationRulesAndManualsInfo') }}</a>
                  </b-form-checkbox>
                  </b-row>

                  <b-row>
                  <b-form-checkbox id="necessary_contacts"
                                   v-model="guideline.necessary_contacts"
                                   v-validate="{ required: true, min_value: 1 }"
                                   data-vv-scope="step.final"
                                   data-vv-name="guideline.necessary_contacts"
                                   value="1"
                                   unchecked-value="0"
                                   :state="errors.first('guideline.necessary_contacts')?'invalid':'success'"
                                   key="necessary-contacts">
                    <var class="text-danger">*</var> {{ $t('message.Guideline.necessaryContacts') }}
                  </b-form-checkbox>
                  </b-row>
                </b-card>

                <div class="card-body" v-if="step < 4">
                  <b-row class="my-1">
                    <b-col sm="3">
                      <var class="text-danger">*</var>{{ $t("message.fieldRequired") }}
                    </b-col>
                  </b-row>
                </div>

                <div class="stepper-container flex-row-reverse" v-if="allowEdit()">
                  <div>
                    <b-button variant="link" class="mr-2" @click="$router.push('/guidelines/')">{{ $t("message.cancel") }}</b-button>

                    <b-button v-if="!guideline.id" variant="outline-secondary" class="mr-2"
                      @click="storeDraft">{{ $t('message.Guideline.saveDraft') }}</b-button>
                    <b-button v-if="canUpdate" variant="outline-secondary" class="mr-2" @click="updateDraft">
                      <template v-if="guideline.phase == guidelinePhases.draft">
                        {{ $t('message.Guideline.updateDraft') }}
                      </template>
                      <template v-else>
                        {{ $t('message.update') }}
                      </template>
                    </b-button>

                    <b-button v-if="step > 0" variant="outline-secondary" class="mr-2" @click="prevStep">{{ $t('message.Guideline.previousStep') }}</b-button>

                    <b-button v-if="step < 6" variant="secondary" class="mr-2" @click="nextStep">{{ $t('message.Guideline.nextStep') }}</b-button>
                    <b-button v-if="step === 6" variant="success" class="mr-2" @click="showPublishInfo">{{ $t('message.Guideline.submit') }}
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        <!-- Modals Component -->
        <b-modal
          id="milestoneModal"
          ref="milestoneModal"
          hide-footer
          :title="milestoneModal.title">

          <b-row class="mb-3">
            <b-col sm="5">
              {{ $t('message.Guideline.Milestones.title') }} <var class="text-danger">*</var>
            </b-col>
            <b-col sm="7">
              <b-form-input
                v-model="milestone.title"
                type="text"
                :placeholder="$t('message.Guideline.Milestones.titlePlaceholder')"
                v-validate="'required'"
                data-vv-name="milestone.title"
                data-vv-scope="milestone"
                :state="errors.has('milestone.title')?false:null"
                ></b-form-input>
            </b-col>
          </b-row>

          <b-row class="mb-3">
            <b-col sm="5">
              {{ $t('message.Guideline.Milestones.budget') }} <var class="text-danger">*</var>
            </b-col>
            <b-col sm="7">
              <b-form-input
                v-model="milestone.budget"
                type="text"
                :placeholder="$t('message.Guideline.Milestones.budget')"
                v-validate="'required|decimal:2'"
                data-vv-name="milestone.budget"
                data-vv-scope="milestone"
                :state="errors.has('milestone.budget')?false:null"
                ></b-form-input>
              <div class="invalid-feedback">{{ $t('message.Guideline.Milestones.budgetInvalid') }}</div>
            </b-col>
          </b-row>

          <b-row class="mb-3">
            <b-col sm="7">
              {{ $t('message.Guideline.Milestones.chooseStartDate') }} <var class="text-danger">*</var>
            </b-col>
            <b-col sm="5">
              <datepicker
                class="w-100"
                :value="milestone.start"
                v-model="milestone.start"
                format="dd-MM-yyyy"
                input-class="form-control"
                :disabledDates="disabledDates"
                >
                </datepicker>

              <input
                type="hidden"
                v-model="milestone.start"
                v-validate="'required'"
                data-vv-name="milestone.start"
                data-vv-scope="milestone"
                />
              <p v-show="errors.has('milestone.start')" class="text-danger">{{ $t("message.chooseProperDate") }}</p>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col>
              <var class="text-danger">*</var>{{ $t("message.fieldRequired") }}
            </b-col>
          </b-row>

          <b-btn class="mt-3" variant="success" block @click="addMilestone">{{  milestoneModal.add?$t("message.add"):$t("message.update") }}</b-btn>
        </b-modal>

        <b-modal
          id="coAuthorModal"
          ref="coAuthorModal"
          hide-footer
          :title="$t('message.Guideline.coAuthorsForm.title')">

          <b-row class="my-1">
            <b-col sm="3"><label class="col-form-label">{{ $t('message.User.firstName') }} <var class="text-danger">*</var></label></b-col>
            <b-col sm="9">
              <b-form-input
                :class="errors.first('coAuthor.first_name')?'is-invalid':''"
                v-model="coAuthor.first_name"
                v-validate="'required'"
                data-vv-name="coAuthor.first_name"
                type="text"
                :placeholder="$t('message.User.firstName')"
                data-vv-scope="coAuthor"
                ></b-form-input>
              <div class="invalid-feedback">
                {{ $t('message.incorrectValue') }}
              </div>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col sm="3"><label class="col-form-label">{{ $t('message.User.lastName') }} <var class="text-danger">*</var></label></b-col>
            <b-col sm="9">
              <b-form-input
                :class="errors.first('coAuthor.last_name')?'is-invalid':''"
                v-model="coAuthor.last_name"
                v-validate="'required'"
                data-vv-name="coAuthor.last_name"
                type="text"
                :placeholder="$t('message.User.lastName')"
                data-vv-scope="coAuthor"
                ></b-form-input>
              <div class="invalid-feedback">
                {{ $t('message.incorrectValue') }}
              </div>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col sm="3"><label class="col-form-label">{{ $t('message.User.gender') }} <var class="text-danger">*</var></label></b-col>
            <b-col sm="9">
              <b-form-radio-group
                v-model="coAuthor.gender"
                :options="[{text: $t('message.User.male'), value: 0}, {text: $t('message.User.female'), value: 1}]"
              >
              </b-form-radio-group>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col sm="3"><label class="col-form-label">{{ $t('message.User.email') }} <var class="text-danger">*</var></label></b-col>
            <b-col sm="9">
              <b-form-input
                :class="errors.first('coAuthor.email')?'is-invalid':''"
                v-model="coAuthor.email"
                v-validate="'required|email'"
                data-vv-name="coAuthor.email"
                type="text"
                :placeholder="$t('message.User.email')"
                data-vv-scope="coAuthor"
                ></b-form-input>
              <div class="invalid-feedback">
                {{ $t('message.incorrectValue') }}
              </div>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col sm="3"><label class="col-form-label">{{ $t('message.User.hospital') }} <var class="text-danger">*</var></label></b-col>
            <b-col sm="9">
              <b-form-input
                :class="errors.first('coAuthor.hospital')?'is-invalid':''"
                v-model="coAuthor.hospital"
                v-validate="'required'"
                data-vv-name="coAuthor.hospital"
                type="text"
                :placeholder="$t('message.User.hospital')"
                data-vv-scope="coAuthor"
                ></b-form-input>
              <div class="invalid-feedback">
                {{ $t('message.incorrectValue') }}
              </div>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col sm="3"><label class="col-form-label">{{ $t('message.User.city') }} <var class="text-danger">*</var></label></b-col>
            <b-col sm="9">
              <b-form-input
                :class="errors.first('coAuthor.city')?'is-invalid':''"
                v-model="coAuthor.city"
                v-validate="'required'"
                data-vv-name="coAuthor.city"
                type="text"
                :placeholder="$t('message.User.city')"
                data-vv-scope="coAuthor"
                ></b-form-input>
              <div class="invalid-feedback">
                {{ $t('message.incorrectValue') }}
              </div>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col sm="3"><label class="col-form-label">{{ $t('message.User.country') }} <var class="text-danger">*</var></label></b-col>
            <b-col sm="9">
              <b-form-input
                :class="errors.first('coAuthor.country')?'is-invalid':''"
                v-model="coAuthor.country"
                v-validate="'required'"
                data-vv-name="coAuthor.country"
                type="text"
                :placeholder="$t('message.User.country')"
                data-vv-scope="coAuthor"
                ></b-form-input>
              <div class="invalid-feedback">
                {{ $t('message.incorrectValue') }}
              </div>
            </b-col>
          </b-row>


          <b-button class="ml-2 mt-2 float-right" variant="success" @click="storeNewUser">
            {{ $t('message.Guideline.coAuthorsForm.save') }}
          </b-button>

          <b-button class="ml-2 mt-2 float-right" variant="danger" @click="$refs.coAuthorModal.hide()">
            {{ $t('message.cancel') }}
          </b-button>
        </b-modal>
      </div>

      <div class="page-container guidelines-editor mt-3">
        <b-card v-if="showCommentSection" :title="$t('message.Comments.title')" class="mb-2">
          <comments :guidelineId="guideline.id" :locked="true"></comments>
        </b-card>
      </div>
    </template>
  </div>
</template>

<script>
import { GUIDELINE_UPDATE, GUIDELINE_ADD, GUIDELINE_REQUEST_BY_ID } from '@/store/actions/guidelines';
import { USER_ADD } from '@/store/actions/user';

import { Guideline, GuidelinePhases, Milestone, CoAuthor } from '@/lib/guideline.js';

import { destinationsOfManuscript, kindsOfWork, typesOfManuscript } from '@/lib/selects.js';

import Milestones from '@/components/Milestones'
import Comments from '@/components/Comments'
import InvolvedParties from '@/components/Guideline/GuidelineEditor/InvolvedParties'

export default {
  name: 'guidelines-editor',
  components: {
    Milestones,
    Comments,
    InvolvedParties
  },
  props: [],
  data () {
    return {
      step: 0,

      milestone_meeting_check: false,
      milestone_meta_check: false,
      milestone_medical_check: false,
      milestone_publication_check: false,

      guideline: new Guideline,

      guidelinePhases: GuidelinePhases,

      typesOfManuscript: typesOfManuscript,
      kindsOfWork: kindsOfWork,
      destinationsOfManuscript: destinationsOfManuscript,

      guidelineTypes: [
        { value: 'AI', label: this.$t('message.Guideline.AI') },
        { value: 'BJ', label: this.$t('message.Guideline.BJ') },
        { value: 'CV', label: this.$t('message.Guideline.CV') },
        { value: 'DO', label: this.$t('message.Guideline.DO') },
        { value: 'ET', label: this.$t('message.Guideline.ET') },
        { value: 'II', label: this.$t('message.Guideline.II') },
        { value: 'NI', label: this.$t('message.Guideline.NI') },
        { value: 'OT', label: this.$t('message.Guideline.OT') },
        { value: 'PA', label: this.$t('message.Guideline.PA') },
        { value: 'PH', label: this.$t('message.Guideline.PH') },
        { value: 'DD', label: this.$t('message.Guideline.DD') },
        { value: 'RP', label: this.$t('message.Guideline.RP') },
        { value: 'RY', label: this.$t('message.Guideline.RY') },
        { value: 'TE', label: this.$t('message.Guideline.TE') },
        { value: 'TY', label: this.$t('message.Guideline.TY') },
        { value: 'MI', label: this.$t('message.Guideline.MI') },
        { value: 'YP', label: this.$t('message.Guideline.YP') },
      ],

      disabledDates: {
        to:  ( d => new Date(d.setDate(d.getDate()-1)) )(new Date) // Disable all dates up to specific date
      },

      budgetFields: [
        {
          key: 'start',
          label: this.$t('message.Guideline.Milestones.dueDate'),
        },
        {
          key: 'title',
        },
        {
          key: 'budget',
        },
        {
          key: 'type',
          label: this.$t('message.Guideline.Milestones.type'),
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ],

      milestone: new Milestone,
      milestoneModal: {
        title: ''
      },

      coAuthor: new CoAuthor,

      showDocInfo: false,

      showCoiSubmit: false,
    };
  },
  computed: {
    metaAnalysisCounter() {
      return this.guideline.budget.filter((arg) => { return arg.type == 'meta' }).length;
    },

    publicationFeeCounter() {
      return this.guideline.budget.filter((arg) => { return arg.type == 'publication' }).length;
    },

    guidelineBudgets() {
      return this.guideline.budget;
    },

    guidelineCreatedAt() {
      if (this.guideline.created_at == null && this.guideline.id == null) {
        return this.moment().format('DD-MM-YYYY HH:mm:ss');
      }

      return this.guideline.created_at;
    },

    guidelinePhaseOptions() {
      return [
        { value: this.guidelinePhases.draft, label: this.$t('message.Guideline.phase' + this.guidelinePhases.draft ) },
        { value: this.guidelinePhases.submitted, label: this.$t('message.Guideline.phase' + this.guidelinePhases.submitted ) },
        { value: this.guidelinePhases.rejected, label: this.$t('message.Guideline.phase' + this.guidelinePhases.rejected ) },
        { value: this.guidelinePhases.formalCheckApproval, label: this.$t('message.Guideline.phase' + this.guidelinePhases.formalCheckApproval ) },
        { value: this.guidelinePhases.preApproved, label: this.$t('message.Guideline.phase' + this.guidelinePhases.preApproved ) },
        { value: this.guidelinePhases.uploadedForGpcReview, label: this.$t('message.Guideline.phase' + this.guidelinePhases.uploadedForGpcReview ) },
        { value: this.guidelinePhases.uploadGpcReviewed, label: this.$t('message.Guideline.phase' + this.guidelinePhases.uploadGpcReviewed ) },
        { value: this.guidelinePhases.uploadGpcReviewedEnd, label: this.$t('message.Guideline.phase' + this.guidelinePhases.uploadGpcReviewedEnd ) },
        { value: this.guidelinePhases.uploadedForReview, label: this.$t('message.Guideline.phase' + this.guidelinePhases.uploadedForReview ) },
        { value: this.guidelinePhases.uploadReviewed, label: this.$t('message.Guideline.phase' + this.guidelinePhases.uploadReviewed).replace('<br>', '') },
        { value: this.guidelinePhases.uploadReviewedEnd, label: this.$t('message.Guideline.phase' + this.guidelinePhases.uploadReviewedEnd).replace('<br>', '') },
        { value: this.guidelinePhases.reviewByNationalDelegates, label: this.$t('message.Guideline.phase' + this.guidelinePhases.reviewByNationalDelegates).replace('<br>', '') },
        { value: this.guidelinePhases.reviewedByNationalDelegates, label: this.$t('message.Guideline.phase' + this.guidelinePhases.reviewedByNationalDelegates).replace('<br>', '') },
        { value: this.guidelinePhases.reviewByNationalDelegatesEnd, label: this.$t('message.Guideline.phase' + this.guidelinePhases.reviewByNationalDelegatesEnd).replace('<br>', '') },
        { value: this.guidelinePhases.gpcFinalApproval, label: this.$t('message.Guideline.phase' + this.guidelinePhases.gpcFinalApproval) },
        { value: this.guidelinePhases.gpcFinalHold, label: this.$t('message.Guideline.phase' + this.guidelinePhases.gpcFinalHold) },
        { value: this.guidelinePhases.boardApproval, label: this.$t('message.Guideline.phase' + this.guidelinePhases.boardApproval) },
        { value: this.guidelinePhases.boardApprovalApproved, label: this.$t('message.Guideline.phase' + this.guidelinePhases.boardApprovalApproved) },
        { value: this.guidelinePhases.boardApprovalOnHold, label: this.$t('message.Guideline.phase' + this.guidelinePhases.boardApprovalOnHold) },
        { value: this.guidelinePhases.finalPublish, label: this.$t('message.Guideline.phase' + this.guidelinePhases.finalPublish) },
      ];
    },

    guidelinePhase: {
      get: function () {
        return {
          value: this.guideline.phase, 
          label: this.$t('message.Guideline.phase' + this.guideline.phase).replace('<br>', '')
        };
      },
      set: function (newValue) {
        this.guideline.phase = newValue.value;
      }
    },

    canUpdate() {
      if (this.isSuperUser) {
        return true;
      }

      return this.guideline && this.guideline.id != null && this.guideline.phase == this.guidelinePhases.draft;
    },

    showCommentSection() {
      return this.guideline.id != null && this.guideline.phase > this.guidelinePhases.draft;
    }
  },
  mounted () {
    if (this.$route.params.id != undefined) {
      this.getData();
    }
  },
  methods: {
    needMilestoneCheck(milestone) {
      return this.guidelineBudgets.filter(x => x.type === milestone).length === 0
    },

    nextStep() {

      //TODO improve the validation with custom rule for veevalidate (if possible)
      if (this.step === 4) {

        if (this.guidelineBudgets.length !== 4) {

            let needsToReturn = false;

          if (this.guidelineBudgets.filter(x => x.type === 'meeting').length === 0) {
            if (!this.milestone_meeting_check) {
                this.$validator.errors.add({
                    id: "milestone.meeting.not_set",
                    field: "milestone.meeting.not_set",
                    msg: "No milestone for meeting has been added. Please confirm.",
                    scope: this.$options.scope,
                });
                needsToReturn = true;
            }
          }

          if (this.guidelineBudgets.filter(x => x.type === 'meta').length === 0) {
            if (!this.milestone_meta_check) {
                this.$validator.errors.add({
                    id: "milestone.meta.not_set",
                    field: "milestone.meta.not_set",
                    msg: "No milestone for meta has been added. Please confirm.",
                    scope: this.$options.scope,
                });
                needsToReturn = true;
            }
          }

          if (this.guidelineBudgets.filter(x => x.type === 'medical').length === 0) {
            if (!this.milestone_medical_check) {
                this.$validator.errors.add({
                    id: "milestone.medical.not_set",
                    field: "milestone.medical.not_set",
                    msg: "No milestone for medical has been added. Please confirm.",
                    scope: this.$options.scope,
                });
                needsToReturn = true;
            }
          }

          if (this.guidelineBudgets.filter(x => x.type === 'publication').length === 0) {
            if (!this.milestone_publication_check) {
                this.$validator.errors.add({
                    id: "milestone.publication.not_set",
                    field: "milestone.publication.not_set",
                    msg: "No milestone for publication has been added. Please confirm.",
                    scope: this.$options.scope,
                });
                needsToReturn = true;
            }
          }

          if (needsToReturn) {
              return;
          }
        }
      }

      this.$validator.validateAll('step.' + this.step).then(valid => {
        if (valid) {
          this.step++;
        }
      });
    },

    prevStep() {
      if (this.step > 0) {
        this.step--;
      }
    },

    setStep(step) {
      if (this.isSuperUser) {
        this.step = step;
      }
    },

    toggleSendCoi() {
      this.guideline.send_coi_email = !this.guideline.send_coi_email;
    },

    showCoiSubmitButton() {
      this.showCoiSubmit = true;
    },

    eventDrop(event) {
      /**
       * looking for event in budget
       */
      let budgetEvent = this.guideline.budget.filter(elem => {
        return elem.id == event.id;
      });

      let currentEvent = budgetEvent.pop();
      currentEvent.start = event.start.format();

      this.$refs.guidelineBudget.$emit('rerender-events');
    },

    wordCounter(val) {
      if (val == null) return 0;

      let words = val.split(/\s+/).filter( el => {return el.length > 0});

      return words.length;
    },

    update(publish) {
      this.$validator.validate('guideline.*').then(result => {
        if (!result) {
          this.$swal({
            title: 'Error!',
            text: this.$t("message.Guideline.fillRequiredFields"),
            type: 'error',
            confirmButtonText: 'OK'
          });
          this.showValidateErrors = true;
        } else {
          let message = this.$t("message.successfullyUpdated")
          if (publish == true) {
            this.guideline.phase = this.guidelinePhases.submitted;
            message = this.$t("message.Guideline.successfullySubmitted")
          }
          this.$store.dispatch(GUIDELINE_UPDATE, this.guideline).then((response) => {
            this.$swal({
              title: this.$t("message.success"),
              text: message,
              type: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              if (publish) {
                this.$router.push('/guidelines/' + this.guideline.id + '/view');
              }
            });

            this.guideline = response.data.data;
          }).catch((error) => {
            this.handleUnauth(error.response.status);
            this.handleForbidden(error.response.status);
            this.handleUnprocessable(error.response);
          });
        }
      });
    },

    store(publish) {
      this.$validator.validate('step.*').then(result => {
        if (!result) {
          this.$swal({
            title: 'Error!',
            text: this.$t("message.Guideline.fillRequiredFields"),
            type: 'error',
            confirmButtonText: 'OK'
          });

          this.showValidateErrors = true;
        } else {
          let message = this.$t("message.Guideline.added");

          if (publish == true) {
            this.guideline.phase = this.guidelinePhases.submitted;
            message = this.$t("message.Guideline.successfullySubmitted")
          }

          this.$store.dispatch(GUIDELINE_ADD, this.guideline).then((response) => {
            this.$swal({
              title: this.$t("message.success"),
              text: message,
              type: 'success',
              confirmButtonText: 'OK'
            });

            if (publish == true) {
              this.$router.push('/guidelines/' + response.data.data.id + '/view' );
            } else {
              this.$router.push('/guidelines/' + response.data.data.id + '/edit' );
            }
          }).catch((error) => {
            this.handleUnauth(error.response.status);
            this.handleUnprocessable(error.response);
            this.handleForbidden(error.response.status);
          });
        }
      });
    },

    storeDraft() {
      this.guideline.draft = true;

      if (!this.isSuperUser) {
        this.guideline.publication_rules = false;
      }

      this.$store.dispatch(GUIDELINE_ADD, this.guideline).then((response) => {
        this.$swal({
          title: this.$t("message.success"),
          text: this.$t("message.Guideline.draftStored"),
          type: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          const guidelineId = response.data.data.id;
          window.location.href = '/guidelines/' + guidelineId + '/edit';
        });
      }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    updateDraft() {
      this.guideline.draft = true;

      if (!this.isSuperUser) {
        this.guideline.publication_rules = false;
      }

      this.$store.dispatch(GUIDELINE_UPDATE, this.guideline).then((response) => {
        this.$swal({
          title: this.$t("message.success"),
          text: this.$t("message.successfullyUpdated"),
          type: 'success',
          confirmButtonText: 'OK'
        })
        this.guideline = response.data.data;

        this.milestone_meeting_check = this.needMilestoneCheck('meeting')?true:false
        this.milestone_meta_check = this.needMilestoneCheck('meta')?true:false
        this.milestone_medical_check = this.needMilestoneCheck('medical')?true:false
        this.milestone_publication_check = this.needMilestoneCheck('publication')?true:false
      }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    getData() {
      this.$store.dispatch(GUIDELINE_REQUEST_BY_ID, this.$route.params.id).then((response) => {
        this.guideline = response.data.data;

        this.milestone_meeting_check = this.needMilestoneCheck('meeting')?true:false
        this.milestone_meta_check = this.needMilestoneCheck('meta')?true:false
        this.milestone_medical_check = this.needMilestoneCheck('medical')?true:false
        this.milestone_publication_check = this.needMilestoneCheck('publication')?true:false
      }).catch((error) => {
          this.handleUnauth(error.response.status);
      });
    },

    allowEdit() {
      if (this.isSuperUser) {
        return true;
      }
      
      return [this.guidelinePhases.draft, this.guidelinePhases.formalCheckApproval].indexOf(this.guideline.phase) != -1;
    },

    editMilestone(id) {
      this.milestone = this.guideline.budget.filter( elem => { return elem.id == id } ).pop();
      this.milestoneModal.add = false;
      this.milestoneModal.title = this.$t('message.Guideline.Milestones.' + this.milestone.type);
      this.$refs.milestoneModal.show();
    },

    showMilestoneModal(type) {
      this.$validator.reset({scope: 'milestone'});
      this.milestone = new Milestone(type);
      this.milestoneModal.title = this.$t('message.Guideline.Milestones.' + type);
      this.milestoneModal.add = true;
      this.$refs.milestoneModal.show();
    },

    addMilestone() {
      this.$validator.validateAll('milestone').then(result => {
        if (result) {
          let message =this.$t("message.successfullyUpdated");

          if (this.guideline.budget.filter( elem => { return elem.id == this.milestone.id }).length == 0) {
            this.guideline.budget.push(this.milestone);
            message =this.$t("message.successfullyAdded");
          }

          this.$refs.milestoneModal.hide();
          this.milestone = new Milestone;
          this.$validator.reset({scope: 'milestone'});
          this.$swal({
            title: this.$t("message.success"),
            text: message,
            type: 'success',
            confirmButtonText: 'OK'
          });
        } else {
          this.$swal({
            title: 'Error!',
            text: this.$t("message.Guideline.fillRequiredFields"),
            type: 'error',
            confirmButtonText: 'OK'
          });
        }
      });
    },

    deleteMilestone(id) {
      let elem = this.guideline.budget.filter( elem => { return elem.id == id });

      if (elem.length > 0) {
        for (var x in elem) {
          if (elem[x].delete != true) {
            elem[x].delete = true;
            elem[x]._rowVariant = 'danger';

            this.$swal({
              toast: true,
              position: 'bottom-end',
              title: this.$t("message.warning"),
              text: this.$t("message.markedToDel"),
              type: 'warning',
              timer: 3000,
              showConfirmButton: false,
            });

          } else {
            elem[x].delete = false;
            elem[x]._rowVariant = null;
          }
        }
      }
    },

    addNewCoAuthor() {
      this.coAuthor = new CoAuthor;
      this.$refs.coAuthorModal.show();
    },

    storeNewUser() {
      this.$validator.validate('coAuthor.*').then(result => {
        if (!result) {
          this.$swal({
            title: 'Error!',
            text: this.$t("message.Guideline.fillRequiredFields"),
            type: 'error',
            confirmButtonText: 'OK'
          });
          this.showValidateErrors = true;
        } else {
          this.$store.dispatch(USER_ADD, this.coAuthor).then(() => {
            this.$refs.coAuthorModal.hide();
          }).catch((error) => {
            this.handleUnauth(error.response.status);
          });
        }
      })
    },

    showPublishInfo() {
      this.$validator.validateAll('step.final').then(result => {
        if (result) {
          this.$swal({
            title: this.$t("message.areYouSure"),
            text: this.$t("message.Guideline.publishWarning"),
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: this.$t("message.yesSubmit"),
            cancelButtonText: this.$t("message.noCancel"),
          }).then((result) => {
            if (result.value) {
              if (this.guideline.id != undefined) {
                return this.update(true);
              } else {
                return this.store(true);
              }
            }
          });
        }
      });
    },
  }
}
</script>

<style scoped lang="scss">
.progress-step-container {
  padding: 40px 0;
  width: 100%;
  display: flex;
  margin-bottom: 10px;
  justify-content: space-around;
  position: relative;
  z-index: 1;
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 57px;
    width: 100%;
    height: 2px;
    background-color: #bbbbbb;
    z-index: -1;
  }
}

.progress-step {
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 140px;
}

.progress-step__value {

  display: flex;
  width: 30px;
  height: 30px;
  font-weight: bold;
  background-color: #dddddd;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: white;
  margin-bottom: 6px;
}

.progress-step__text {
  font-size: 0.7rem;
  //text-transform: uppercase;
  color: #dddddd;
}

.progress-step--passed {

  .progress-step__value{
    background-color: #1dc9b7;
  }

  .progress-step__text {
    color: #333333;
  }
}
</style>

